import { ViewModelBase } from "../../Core/ViewModels/ViewModelBase";
import { FieldType, getHistory } from "../../Core/Utils/Utils";
import { action, computed, observable } from "mobx";
import { BuyModel, BuyModelDTO, PaymentResultAction } from "../../Custom/Models/BuyModel";
import { IProperty } from "../Models/IProperty";
import { ApiResult } from "../../Core/Models";
import { CardPaymentModel, CardPaymentModelDTO } from "../Models/CardPaymentModel";
import { StripePaymentRequest } from "Custom/Models";
import { StoresInstance } from "Custom/Stores";

declare var Stripe: any;

const DDC_ORIGIN_TEST = "https://centinelapistag.cardinalcommerce.com";
const DDC_ORIGIN_LIVE = "https://centinelapi.cardinalcommerce.com";

export class BuyViewModel extends ViewModelBase<BuyModel> {
    private static _instance: BuyViewModel;
    public static get Instance() {
        return this._instance || (this._instance = new this());
    }

    @observable
    public errorMessage = "";

    constructor() {
        super(new BuyModel());
        this.setDecorators(BuyModel);
        getHistory().listen(change => {
            const lowerPathname = change.pathname.toLowerCase();
            if (!lowerPathname.includes("/buy") && !lowerPathname.includes("/checkout")) {
                this.resetPurchase();
            }
        });
    }

    @action
    public resetPurchase() {
        this.errorMessage = "";
        this.setValue("propertyId", "");
        this.setValue("paymentSuccess", false);
        this.setValue("stripePayRequestId", undefined);
    }

    private async doPayment(buyModelDTO: BuyModelDTO): Promise<boolean> {
        try {
            this.setErrorMessage("");
            const response = await this.Post<PaymentResponse>("/api/pay/payment", buyModelDTO);
            if (response && response.wasSuccessful) {
                let responsePayload = response.payload;
                this.setValue("stripePayRequestId", responsePayload?.stripePayRequest?.id);

                const action = responsePayload?.action ?? PaymentResultAction.None;
                this.setValue("paymentResultAction", action);
                if (action === PaymentResultAction.None) {
                    this.setValue("paymentSuccess", true);
                    return true;
                }

                this.setValue("paymentSuccess", false);
                return true;
            } else if (response && response.errors && response.errors[0] && response.errors[0].message) {
                this.setErrorMessage(response.errors[0].message);
                this.setValue("paymentSuccess", false);
            } else {
                this.unknownError();
            }
        } catch (error) {
            console.error("Payment error:", error);
            this.unknownError();
        }
        return false;
    }

    // public applePay = (result: any, completion: any) => {
    //     this.doPayment("applepay", result.token.id).then(success => {
    //         if (success) {
    //             completion(Stripe.ApplePaySession.STATUS_SUCCESS);
    //             //getHistory().push("/tickets");
    //         } else {
    //             completion(Stripe.ApplePaySession.STATUS_FAILURE);
    //         }
    //     });
    // };

    // public googlePay = (paymentData: any) => {
    //     this.doPayment("googlepay", JSON.parse(paymentData.paymentMethodData.tokenizationData.token).id).then(success => {
    //         if (success) {
    //             //getHistory().push("/tickets");
    //         } else {
    //         }
    //     });
    // };

    public cardPayment = async (buyModelDTO: BuyModelDTO, showChallenge: (show: boolean) => void, challengeRef: HTMLDivElement): Promise<boolean> => {
        const success = await this.doPayment(buyModelDTO);
        if (!success) return false;

        const paymentResultAction: PaymentResultAction = this.getValue("paymentResultAction");
        switch (paymentResultAction) {
            case PaymentResultAction.DeviceDataCollection:
                const collectionReference = await new Promise<string | undefined>(resolve => {
                    let ddcComplete = false;
                    const ddcIframe = document.createElement("iframe");
                    ddcIframe.src = document.location.origin + "/api/pay/devicedatacollection";
                    ddcIframe.style.display = "none";

                    let cleanup: (() => void) | undefined;
                    const timeout = setTimeout(() => {
                        if (!ddcComplete) {
                            resolve(undefined);
                        }
                        cleanup?.();
                    }, 10 * 1000);

                    const ddcOrigin = StoresInstance.domain.AccountStore.WorldpayTestEnvironment ? DDC_ORIGIN_TEST : DDC_ORIGIN_LIVE;

                    const handleMessage = (event: MessageEvent) => {
                        let msgData: any;
                        try {
                            msgData = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
                        } catch {
                            return;
                        }

                        if (event.origin === document.location.origin && msgData.error !== undefined) {
                            console.error("DDC error:", msgData.error);
                            if (!ddcComplete) {
                                resolve(undefined);
                            }
                            cleanup?.();
                        }

                        const msgType = msgData.messageType ?? msgData.MessageType;
                        if (event.origin === ddcOrigin && msgType === "profile.completed") {
                            if (!ddcComplete) {
                                if (msgData.Status === true) {
                                    resolve(msgData.SessionId);
                                } else {
                                    resolve(undefined);
                                }
                            }
                            cleanup?.();
                        }
                    };

                    cleanup = () => {
                        if (ddcComplete) return;
                        ddcComplete = true;
                        clearTimeout(timeout);
                        window.removeEventListener("message", handleMessage);
                        document.body.removeChild(ddcIframe);
                    };

                    window.addEventListener("message", handleMessage);
                    document.body.appendChild(ddcIframe);
                });

                return await this.cardPayment(
                    {
                        ...buyModelDTO,
                        stripePayRequestId: this.getValue("stripePayRequestId"),
                        collectionReference: collectionReference,
                        completedAction: PaymentResultAction.DeviceDataCollection,
                    },
                    showChallenge,
                    challengeRef,
                );
            case PaymentResultAction.Challenge:
                const success = await new Promise(resolve => {
                    const challengeIframe = document.createElement("iframe");
                    challengeIframe.src = document.location.origin + "/api/pay/threedschallenge";
                    challengeIframe.height = "400";
                    challengeIframe.width = "500";
                    challengeIframe.style.border = "none";
                    challengeIframe.style.maxWidth = "min(400px, 95vw)";
                    challengeIframe.style.maxHeight = "min(500px, 90vh)";
                    challengeIframe.style.background = "white";

                    let cleanup: (() => void) | undefined;
                    const handleMessage = (event: MessageEvent) => {
                        if (event.origin !== document.location.origin) return;
                        if (event.data === "3DS_COMPLETE") {
                            cleanup?.();
                            resolve(true);
                        } else if (event.data?.error !== undefined) {
                            cleanup?.();
                            console.error("3DS challenge error:", event.data.error);
                            this.setErrorMessage(event.data.error);
                            resolve(false);
                        }
                    };

                    cleanup = () => {
                        showChallenge(false);
                        window.removeEventListener("message", handleMessage);
                        challengeRef.removeChild(challengeIframe);
                    };

                    window.addEventListener("message", handleMessage);
                    challengeRef.appendChild(challengeIframe);
                    showChallenge(true);
                });

                if (success) {
                    return await this.cardPayment(
                        {
                            ...buyModelDTO,
                            stripePayRequestId: this.getValue("stripePayRequestId"),
                            completedAction: PaymentResultAction.Challenge,
                        },
                        showChallenge,
                        challengeRef,
                    );
                } else {
                    return false;
                }
            default:
                return true;
        }
    };

    private unknownError = () => {
        this.setErrorMessage("An unknown error occurred during payment processing.");
    };

    @action
    public setErrorMessage(msg: string) {
        this.errorMessage = msg;
    }

    @computed
    public get price() {
        return this.getPrice(false) as number;
    }

    public getPrice(formatted: boolean) {
        let price = 0;
        price = this.price || 0;

        if (!formatted) return price;

        return `£${price.toFixed(2)}`;
    }

    public isFieldValid(fieldName: keyof FieldType<BuyModel>, value: any): boolean {
        let { isValid, errorMessage } = this.validateDecorators(fieldName);

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    // public afterUpdate(fieldName: keyof FieldType<BuyModel>, value: any) {
    //     if (fieldName === "route" && value) {
    //         this.fetchRouteFares(value);
    //     }
    // }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}

type PaymentResponse = {
    action: PaymentResultAction;
    stripePayRequest: StripePaymentRequest;
};
